/**
 * Add Card Form Component
 * Uses Stripe Elements (PaymentElement) to securely collect card details.
 * Calls getgeneral type:"sc" to get SetupIntent client_secret + publishable key,
 * then renders Stripe PaymentElement for PCI-compliant card collection.
 */
import React, { useState, useEffect } from 'react';
import { CreditCard, X, Loader2, AlertTriangle } from 'lucide-react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { API_BASE_URL } from '../../config/api';

const appearance = {
  theme: 'night',
  variables: {
    colorPrimary: '#00E878',
    colorBackground: '#1A1A1A',
    colorText: '#ffffff',
    colorDanger: '#ef4444',
    fontFamily: 'Outfit, system-ui, sans-serif',
    borderRadius: '12px',
  },
};

const StripeCardForm = ({ onSuccess, onCancel }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setProcessing(true);
    setError(null);

    try {
      const result = await stripe.confirmSetup({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/app/account/payment?setup=success`,
        },
      });
      if (result.error) {
        setError(result.error.message);
      } else {
        onSuccess && onSuccess();
      }
    } catch (err) {
      setError(err.message || 'Failed to save card');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <PaymentElement options={{ layout: 'tabs' }} />
      {error && (
        <div className="mt-4 p-3 bg-red-500/10 border border-red-500/30 rounded-xl flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 text-red-400 mt-0.5 shrink-0" />
          <p className="text-sm text-red-400">{error}</p>
        </div>
      )}
      <div className="flex gap-3 pt-4 mt-4">
        <button type="button" onClick={onCancel} disabled={processing}
          className="flex-1 py-3 border border-white/20 text-white rounded-xl hover:bg-white/5 transition-colors disabled:opacity-50">
          Cancel
        </button>
        <button type="submit" disabled={!stripe || processing}
          className="flex-1 py-3 bg-gradient-to-r from-accent to-accent-dark text-dark font-semibold rounded-xl hover:shadow-lg hover:shadow-accent/30 transition-all disabled:opacity-50 flex items-center justify-center gap-2">
          {processing ? (<><Loader2 size={18} className="animate-spin" />Saving...</>) : (<><CreditCard size={18} />Save Card</>)}
        </button>
      </div>
    </form>
  );
};

const AddCardForm = ({ onSuccess, onCancel }) => {
  const [stripePromise, setStripePromise] = useState(null);
  const [clientSecret, setClientSecret] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const email = sessionStorage.getItem('account_email') || '';
  const pwd = sessionStorage.getItem('account_pwd') || '';

  useEffect(() => { initializeStripe(); }, []);

  const initializeStripe = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_BASE_URL}/general/getgeneral`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type: 'sc', email, pwd }),
      });
      const result = await response.json();

      if (!result.success) {
        setError(result.message || 'Failed to initialize payment setup');
        return;
      }

      const { pk, cs } = result.data || {};

      if (!pk) {
        setError('Stripe is not configured. Please contact support.');
        return;
      }

      if (!cs) {
        setError('Failed to create payment setup session');
        return;
      }

      setStripePromise(loadStripe(pk));
      setClientSecret(cs);
    } catch (err) {
      console.error('Error initializing Stripe:', err);
      setError('Failed to initialize payment form. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-dark-100 rounded-2xl border border-white/10 p-5">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-white">Add Payment Method</h3>
        <button onClick={onCancel} className="p-1 text-gray-400 hover:text-white transition-colors"><X size={20} /></button>
      </div>
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 text-accent animate-spin mr-3" />
          <span className="text-gray-400">Loading payment form...</span>
        </div>
      ) : error ? (
        <div className="text-center py-8">
          <AlertTriangle className="w-10 h-10 text-red-400 mx-auto mb-3" />
          <p className="text-red-400 mb-4">{error}</p>
          <button onClick={initializeStripe} className="px-4 py-2 border border-white/20 text-white rounded-lg hover:bg-white/5 transition-colors">Try Again</button>
        </div>
      ) : clientSecret && stripePromise ? (
        <Elements stripe={stripePromise} options={{ clientSecret, appearance }}>
          <StripeCardForm onSuccess={onSuccess} onCancel={onCancel} />
        </Elements>
      ) : null}
      <div className="mt-4 pt-4 border-t border-white/10">
        <p className="text-xs text-gray-500 text-center">🔒 Secured by Stripe. Your card details are encrypted and never stored on our servers.</p>
      </div>
    </div>
  );
};

export default AddCardForm;
